import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  TablePagination,
} from '@mui/material';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import type { EmployeeCheckIn, CheckInAuditEvent, EmployeeCheckInConfig } from '../types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { formatDate, formatCalendarDateIso, parseUtcDate, utcMs } from '../utils/dateFormat';
import { AppDatePicker } from '../components/AppDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { createShouldDisableDate } from '../utils/dateValidation';
import { useDateLocale } from '../hooks/useDateLocale';
import { usePublicHolidays } from '../hooks/usePublicHolidays';
import { formatCheckInStatus, getCheckInStatusColor, formatCheckInStatusInText } from '../utils/checkInStatus';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, couldNotConfirm, couldNotRecord } from '../utils/userFacingCopy';
import { employeeManagerOptedOutMessage } from '../utils/checkInOptOutCopy';

export default function CheckInsPage() {
  const navigate = useNavigate();
  const { id: checkInIdFromUrl } = useParams<{ id: string }>();
  const { user, loading: authLoading, displayTimeZoneId } = useAuth();
  const country = useDateLocale();
  const { isHoliday } = usePublicHolidays(country);
  const shouldDisableDate = createShouldDisableDate(isHoliday);
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view');

  // Check if user has manager role (including TeamLead)
  const hasManagerRole = user?.roles?.some((role: string) =>
    role === 'Manager' || role === 'SeniorManager' || role === 'Administrator' || role === 'TeamLead'
  ) ?? false;

  // Determine view: 'my' = employee view, otherwise = manager view
  // If non-manager accesses without view=my, default to employee view
  // If manager accesses without view param, default to manager view (team check-ins)
  const isEmployeeView = viewParam === 'my' || (!hasManagerRole && viewParam !== 'team');

  // If non-manager tries to access manager view, redirect to employee view.
  // Guard with authLoading so we don't redirect while roles are still being fetched —
  // without this guard, managers get incorrectly redirected to ?view=my on page load
  // because hasManagerRole is temporarily false while the auth context initialises.
  useEffect(() => {
    if (!authLoading && !hasManagerRole && viewParam !== 'my') {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('view', 'my');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [authLoading, hasManagerRole, viewParam, searchParams, setSearchParams]);

  const [checkIns, setCheckIns] = useState<EmployeeCheckIn[]>([]);
  const [managerOptedOutOfBuiltInCheckIns, setManagerOptedOutOfBuiltInCheckIns] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedCheckIn, setSelectedCheckIn] = useState<EmployeeCheckIn | null>(null);
  const [auditEvents, setAuditEvents] = useState<CheckInAuditEvent[]>([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [notes, setNotes] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [markingDecision, setMarkingDecision] = useState(false);
  const [decisionComments, setDecisionComments] = useState('');
  const [decisionType, setDecisionType] = useState<'Occurred' | 'Missed'>('Occurred');
  const [actualOccurrenceDate, setActualOccurrenceDate] = useState<Date | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<'name' | 'dueDate' | 'status' | null>('dueDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const loadCheckIns = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.get<EmployeeCheckIn[]>(API_ENDPOINTS.CHECK_INS.MY);
      setCheckIns(response.data);
      try {
        const configResponse = await apiService.get<EmployeeCheckInConfig | null>(API_ENDPOINTS.CHECK_INS.CONFIG_MY);
        setManagerOptedOutOfBuiltInCheckIns(!!configResponse.data?.managerOptedOutOfBuiltInCheckIns);
      } catch {
        setManagerOptedOutOfBuiltInCheckIns(false);
      }
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotLoad('check-ins')));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCheckIns();
  }, [loadCheckIns]);

  const loadAuditTrailForCheckIn = useCallback(async (checkIn: EmployeeCheckIn) => {
    setLoadingAudit(true);
    setAuditEvents([]);
    try {
      const response = await apiService.get<CheckInAuditEvent[]>(
        API_ENDPOINTS.CHECK_INS.AUDIT_TRAIL(checkIn.id)
      );
      setAuditEvents(response.data);
    } catch (err: unknown) {
      console.error('Failed to load audit trail', err);
    } finally {
      setLoadingAudit(false);
    }
  }, []);

  // When opening from email link (/checkins/:id), open the detail dialog for that check-in
  useEffect(() => {
    if (!checkInIdFromUrl || loading || !checkIns.length) return;
    const match = checkIns.find((c) => c.id === checkInIdFromUrl);
    if (match && user && (match.managerId === user.id || match.employeeId === user.id)) {
      setSelectedCheckIn(match);
      setDetailDialogOpen(true);
      loadAuditTrailForCheckIn(match);
      // Switch to manager view if this check-in is for the current user as manager
      if (match.managerId === user.id && hasManagerRole && viewParam !== 'team') {
        const next = new URLSearchParams(searchParams);
        next.set('view', 'team');
        setSearchParams(next, { replace: true });
      }
      // Clear URL so refreshing doesn't reopen dialog (optional; keeps link shareable)
      navigate('/checkins', { replace: true });
    }
  }, [checkInIdFromUrl, loading, checkIns, user, hasManagerRole, viewParam, searchParams, setSearchParams, navigate, loadAuditTrailForCheckIn]);

  const filteredCheckIns = useMemo(() => {
    if (!user) return [];

    // Filter by view (manager vs employee)
    let filtered = checkIns.filter(ci => {
      if (isEmployeeView) {
        // Employee view - show only check-ins for this employee
        return ci.employeeId === user.id;
      } else {
        // Manager view - show only check-ins for this manager
        return ci.managerId === user.id;
      }
    });

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(ci => ci.status === statusFilter);
    }

    // Apply search filter (search by counterpart name or due date - status is filtered separately)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(ci => {
        const counterpartName = isEmployeeView
          ? ci.managerName.toLowerCase()
          : ci.employeeName.toLowerCase();
        const dueDateStr = formatDate(ci.dueDate).toLowerCase();

        return (
          counterpartName.includes(query) ||
          dueDateStr.includes(query)
        );
      });
    }

    return filtered;
  }, [checkIns, isEmployeeView, user, statusFilter, searchQuery]);

  const handleSort = (field: 'name' | 'dueDate' | 'status') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: 'name' | 'dueDate' | 'status') => {
    if (sortField !== field) {
      return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />;
    }
    return sortDirection === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: 16 }} />
      : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
  };

  const sortedCheckIns = useMemo(() => {
    if (!sortField) {
      return filteredCheckIns;
    }

    return [...filteredCheckIns].sort((a, b) => {
      if (sortField === 'name') {
        // Sort by employee name in manager view, manager name in employee view
        const aStr = (isEmployeeView ? a.managerName : a.employeeName) || '';
        const bStr = (isEmployeeView ? b.managerName : b.employeeName) || '';
        return sortDirection === 'asc'
          ? aStr.localeCompare(bStr)
          : bStr.localeCompare(aStr);
      }

      if (sortField === 'dueDate') {
        const aTime = utcMs(a.dueDate);
        const bTime = utcMs(b.dueDate);
        return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
      }

      if (sortField === 'status') {
        const statusOrder: Record<string, number> = {
          'Scheduled': 1,
          'AwaitingManagerDecision': 2,
          'AwaitingEmployeeAcknowledgment': 3,
          'LatePendingAcknowledgment': 4,
          'MissedPendingAcknowledgment': 5,
          'Completed': 6,
          'Cancelled': 7,
        };
        const aOrder = statusOrder[a.status] ?? 999;
        const bOrder = statusOrder[b.status] ?? 999;
        return sortDirection === 'asc' ? aOrder - bOrder : bOrder - aOrder;
      }

      return 0;
    });
  }, [filteredCheckIns, sortField, sortDirection, isEmployeeView]);

  const paginatedCheckIns = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedCheckIns.slice(start, start + rowsPerPage);
  }, [sortedCheckIns, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, statusFilter]);

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleOpenDetail = async (checkIn: EmployeeCheckIn) => {
    setSelectedCheckIn(checkIn);
    setNotes('');
    setDecisionComments('');
    setDecisionType('Occurred');
    setActualOccurrenceDate('');
    setDetailDialogOpen(true);
    await loadAuditTrailForCheckIn(checkIn);
  };

  const handleConfirm = async () => {
    if (!selectedCheckIn) return;

    try {
      setConfirming(true);
      setError(''); // Clear any previous errors
      const isManager = selectedCheckIn.managerId === user?.id;
      const endpoint = isManager
        ? API_ENDPOINTS.CHECK_INS.CONFIRM_OCCURRENCE_MANAGER(selectedCheckIn.id)
        : API_ENDPOINTS.CHECK_INS.CONFIRM_OCCURRENCE_EMPLOYEE(selectedCheckIn.id);

      // Only send notes for manager confirmations, not employee acknowledgments
      const payload = isManager ? { notes: notes || undefined } : {};
      await apiService.post(endpoint, payload);
      setDetailDialogOpen(false);
      setNotes('');
      await loadCheckIns();
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotConfirm('the check-in')));
      console.error('Failed to confirm check-in:', err);
    } finally {
      setConfirming(false);
    }
  };

  const handleMarkDecision = async () => {
    if (!selectedCheckIn) return;

    if (decisionType === 'Occurred') {
      if (!actualOccurrenceDate) {
        setError('Please enter the actual date the check-in occurred.');
        return;
      }
    } else {
      if (!decisionComments.trim()) {
        setError('Manager must add notes when marking a check-in as missed.');
        return;
      }
    }

    const dueDate = parseUtcDate(selectedCheckIn.dueDate)!;
    const actualDate = actualOccurrenceDate;
    const decision: 'OnTime' | 'Late' | 'Missed' = decisionType === 'Missed'
      ? 'Missed'
      : actualDate && actualDate <= dueDate ? 'OnTime' : 'Late';

    try {
      setMarkingDecision(true);
      setError('');
      await apiService.post(API_ENDPOINTS.CHECK_INS.MARK_DECISION(selectedCheckIn.id), {
        decision,
        comments: decisionType === 'Missed' ? decisionComments.trim() : (decisionComments.trim() || undefined),
        actualOccurrenceDate: decisionType === 'Occurred' && actualOccurrenceDate ? formatCalendarDateIso(actualOccurrenceDate) : undefined
      });
      setDetailDialogOpen(false);
      setDecisionComments('');
      setDecisionType('Occurred');
      setActualOccurrenceDate(null);
      await loadCheckIns();
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotRecord('the check-in decision')));
    } finally {
      setMarkingDecision(false);
    }
  };

  const isManager = selectedCheckIn && user ? selectedCheckIn.managerId === user.id : false;
  const isEmployee = selectedCheckIn && user ? selectedCheckIn.employeeId === user.id : false;

  // Manager can confirm/record decision only in states that allow it
  // Note: Manager uses mark-decision endpoint, not confirm-occurrence
  const canManagerConfirm = false; // Manager uses mark-decision, not confirm-occurrence

  // Employee can only acknowledge AFTER manager has recorded a decision
  // Employee can acknowledge when status indicates manager has made a decision:
  // - AwaitingEmployeeAcknowledgment (on-time)
  // - LatePendingAcknowledgment (late)
  // - MissedPendingAcknowledgment (missed)
  const canEmployeeAcknowledge = isEmployee &&
    selectedCheckIn?.managerOccurrenceConfirmedAt && // Manager must have recorded decision first
    !selectedCheckIn?.employeeOccurrenceConfirmedAt && // Employee hasn't acknowledged yet
    (selectedCheckIn?.status === 'AwaitingEmployeeAcknowledgment' ||
     selectedCheckIn?.status === 'LatePendingAcknowledgment' ||
     selectedCheckIn?.status === 'MissedPendingAcknowledgment');

  const canConfirm = canManagerConfirm || canEmployeeAcknowledge;

  // Manager can record decision when check-in is Scheduled or AwaitingManagerDecision (e.g. past due)
  const canManagerRecordDecision = isManager &&
    !selectedCheckIn?.managerOccurrenceConfirmedAt &&
    (selectedCheckIn?.status === 'Scheduled' || selectedCheckIn?.status === 'AwaitingManagerDecision' || selectedCheckIn?.status === 'Pending');

  // Debug logging (can be removed later)
  if (selectedCheckIn && isEmployee) {
    console.log('Employee acknowledgment check:', {
      isEmployee,
      managerOccurrenceConfirmedAt: selectedCheckIn.managerOccurrenceConfirmedAt,
      employeeOccurrenceConfirmedAt: selectedCheckIn.employeeOccurrenceConfirmedAt,
      status: selectedCheckIn.status,
      canEmployeeAcknowledge,
      canConfirm,
      canManagerRecordDecision
    });
  }

  const needsAcknowledgment = isEmployee &&
    (selectedCheckIn?.status === 'MissedPendingAcknowledgment' ||
     selectedCheckIn?.status === 'LatePendingAcknowledgment') &&
    selectedCheckIn?.managerOccurrenceConfirmedAt &&
    !selectedCheckIn?.employeeOccurrenceConfirmedAt;

  const getEventTypeLabel = (eventType: string) => {
    const labels: Record<string, string> = {
      ConfigCreated: 'Config Created',
      ConfigUpdated: 'Config Updated',
      ConfigDeactivated: 'Config Deactivated',
      CheckInScheduled: 'Check-in Scheduled',
      ReminderSent: 'Reminder Sent',
      ReminderSentUrgent: 'Urgent Reminder Sent',
      ManagerSchedulingAcknowledged: 'Manager confirmed check-in setup',
      ManagerOccurrenceConfirmed: 'Manager Occurrence Confirmed',
      EmployeeOccurrenceConfirmed: 'Employee Occurrence Confirmed',
      ManagerMarkedMissed: 'Manager Marked Missed',
      ManagerMarkedLate: 'Manager Marked Late',
      EmployeeAcknowledgedMissed: 'Employee Acknowledged Missed',
      EmployeeAcknowledgedLate: 'Employee Acknowledged Late',
      CheckInCancelledDueToFrequencyRemoved: 'Check-in cancelled (frequency removed)',
      CheckInCancelledDueToManagerOptOut: 'Check-in cancelled (manager opted out)',
      StatusChanged: 'Status Changed',
      NotesUpdated: 'Notes Updated',
    };

    // If we have a custom label, use it; otherwise auto-format camelCase
    if (labels[eventType]) {
      return labels[eventType];
    }

    // Auto-format: Add spaces before capital letters and capitalize first letter of each word
    return eventType
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ ml: -1 }} title="Back">
            <ArrowBackIcon />
          </IconButton>
          <CheckCircleIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
            {isEmployeeView ? 'Check-Ins' : 'Check-Ins'}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          {isEmployeeView
            ? (managerOptedOutOfBuiltInCheckIns
                ? employeeManagerOptedOutMessage
                : 'View and manage your scheduled check-ins with your manager. Check-ins help track progress and provide regular feedback throughout the review cycle.')
            : 'View and manage scheduled check-ins with your team members. Record whether check-ins occurred on-time, were late, or were missed.'}
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Search and Filters Card - Outside the table */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <TextField
                    sx={{ flex: 1, minWidth: 200 }}
                    label={`Search ${isEmployeeView ? 'manager' : 'employee'} check-ins`}
                    placeholder={`Search by ${isEmployeeView ? 'manager' : 'employee'} name or due date...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleClearSearch}
                            edge="end"
                            sx={{ mr: -1 }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status"
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="Scheduled">Scheduled</MenuItem>
                      <MenuItem value="AwaitingManagerDecision">Awaiting Manager Decision</MenuItem>
                      <MenuItem value="AwaitingEmployeeAcknowledgment">Awaiting Employee</MenuItem>
                      <MenuItem value="LatePendingAcknowledgment">Late - Pending</MenuItem>
                      <MenuItem value="MissedPendingAcknowledgment">Missed - Pending</MenuItem>
                      <MenuItem value="Completed">Completed</MenuItem>
                      <MenuItem value="Cancelled">Cancelled (frequency removed)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                {(searchQuery.trim() !== '' || statusFilter !== 'all') && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    {searchQuery.trim() !== '' && (
                      <Chip
                        label={`Search: "${searchQuery}"`}
                        onDelete={handleClearSearch}
                        size="small"
                        color="primary"
                      />
                    )}
                    {statusFilter !== 'all' && (
                      <Chip
                        label={`Status: ${formatCheckInStatus(statusFilter)}`}
                        onDelete={() => setStatusFilter('all')}
                        size="small"
                        color="info"
                      />
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Table Card - aligned with Reviews to Manage, Your team and their teams, etc. */}
            <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2 }}>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <TableContainer sx={{ borderTop: 0 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('name')}>
                            <strong>{isEmployeeView ? 'Manager' : 'Employee'}</strong>
                            {getSortIcon('name')}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('dueDate')}>
                            <strong>Due Date</strong>
                            {getSortIcon('dueDate')}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('status')}>
                            <strong>Status</strong>
                            {getSortIcon('status')}
                          </Box>
                        </TableCell>
                        <TableCell align="right"><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedCheckIns.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4, border: 'none' }}>
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                              <EventNoteIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                              <Typography variant="h6" color="text.secondary" gutterBottom>
                                {checkIns.length === 0
                                  ? 'No check-ins found.'
                                  : 'No check-ins match your search or filters.'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {checkIns.length === 0
                                  ? (isEmployeeView
                                      ? (managerOptedOutOfBuiltInCheckIns
                                          ? employeeManagerOptedOutMessage
                                          : 'You don\'t have any check-ins scheduled with your manager yet. Check-ins will appear here once your manager configures them.')
                                      : 'You don\'t have any check-ins scheduled with your direct reports yet. Check-ins will appear here once they are configured and scheduled.')
                                  : 'Try adjusting your search or filter criteria.'}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedCheckIns.map((checkIn) => (
                          <TableRow key={checkIn.id}>
                            <TableCell>
                              {isEmployeeView ? checkIn.managerName : checkIn.employeeName}
                            </TableCell>
                            <TableCell>{formatDate(checkIn.dueDate)}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                                <Chip
                                  label={formatCheckInStatus(checkIn.status)}
                                  color={getCheckInStatusColor(checkIn.status)}
                                  size="small"
                                />
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleOpenDetail(checkIn)}
                              >
                                View / Confirm
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={sortedCheckIns.length}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}
                />
              </CardContent>
            </Card>
          </>
        )}

        {/* Detail Dialog */}
        <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Check-In Details
            {selectedCheckIn && (
              <Chip
                label={formatCheckInStatus(selectedCheckIn.status)}
                color={getCheckInStatusColor(selectedCheckIn.status)}
                size="small"
                sx={{ ml: 2 }}
              />
            )}
          </DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {selectedCheckIn && (
              <Box sx={{ mt: 2 }} key={displayTimeZoneId}>
                <Typography variant="subtitle2" color="text.secondary">
                  Manager
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedCheckIn.managerName}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary">
                  Employee
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedCheckIn.employeeName}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary">
                  Due Date
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {formatDate(selectedCheckIn.dueDate)}
                </Typography>

                {selectedCheckIn.managerOccurrenceConfirmedAt && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Manager Recorded Decision
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {formatDate(selectedCheckIn.managerOccurrenceConfirmedAt, { includeTime: true })}
                    </Typography>
                    {(() => {
                      const status = selectedCheckIn.status;
                      const decisionType = status === 'AwaitingEmployeeAcknowledgment'
                        ? 'On Time'
                        : status === 'LatePendingAcknowledgment'
                        ? 'Late'
                        : status === 'MissedPendingAcknowledgment'
                        ? 'Missed'
                        : null;

                      if (decisionType) {
                        return (
                          <Box sx={{ mb: selectedCheckIn.managerNotes ? 1 : 0 }}>
                            <Chip
                              label={`Recorded as: ${decisionType}`}
                              size="small"
                              color={
                                decisionType === 'Missed' ? 'error' :
                                decisionType === 'Late' ? 'warning' :
                                'success'
                              }
                              sx={{ fontWeight: 500 }}
                            />
                          </Box>
                        );
                      }
                      return null;
                    })()}
                    {selectedCheckIn.managerNotes && (
                      <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.100', borderRadius: 1, border: 1, borderColor: (selectedCheckIn.status === 'MissedPendingAcknowledgment' || selectedCheckIn.status === 'LatePendingAcknowledgment') ? 'error.main' : 'divider' }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                          {(selectedCheckIn.status === 'MissedPendingAcknowledgment' || selectedCheckIn.status === 'LatePendingAcknowledgment')
                            ? 'Manager Comments (Required Acknowledgment):'
                            : 'Manager Notes:'}
                        </Typography>
                        <Typography variant="body2">
                          {selectedCheckIn.managerNotes}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {selectedCheckIn.employeeOccurrenceConfirmedAt && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Employee Acknowledged
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(selectedCheckIn.employeeOccurrenceConfirmedAt, { includeTime: true })}
                    </Typography>
                    {selectedCheckIn.employeeNotes && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Notes: {selectedCheckIn.employeeNotes}
                      </Typography>
                    )}
                  </Box>
                )}

                {canManagerRecordDecision && (
                  <Box sx={{ mt: 3, mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                      Record Check-In Decision
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Record whether the check-in occurred (and when) or was missed. The employee will acknowledge as usual.
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Decision</InputLabel>
                      <Select
                        value={decisionType}
                        onChange={(e) => setDecisionType(e.target.value as 'Occurred' | 'Missed')}
                        label="Decision"
                      >
                        <MenuItem value="Occurred">Occurred (it happened — enter actual date)</MenuItem>
                        <MenuItem value="Missed">Missed (did not happen)</MenuItem>
                      </Select>
                    </FormControl>
                    {decisionType === 'Occurred' && (
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <AppDatePicker
                          label="Actual date the check-in occurred"
                          value={actualOccurrenceDate}
                          onChange={(date) => setActualOccurrenceDate(date)}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              required: true,
                              error: !actualOccurrenceDate,
                              helperText: !actualOccurrenceDate ? 'Required' : 'On time vs late is derived from this date and the due date.',
                            },
                          }}
                          shouldDisableDate={shouldDisableDate}
                          sx={{ mb: 2 }}
                        />
                      </LocalizationProvider>
                    )}
                    <TextField
                      fullWidth
                      multiline
                      rows={decisionType === 'Missed' ? 4 : 2}
                      label={decisionType === 'Occurred' ? 'Notes (optional)' : 'Notes (required)'}
                      value={decisionComments}
                      onChange={(e) => setDecisionComments(e.target.value)}
                      placeholder={decisionType === 'Missed' ? 'Please explain why this check-in was missed...' : 'Add any notes (optional)...'}
                      required={decisionType === 'Missed'}
                      error={decisionType === 'Missed' && !decisionComments.trim()}
                      helperText={decisionType === 'Missed' && !decisionComments.trim() ? 'Manager must add notes when marking as missed' : ''}
                    />
                  </Box>
                )}

                {needsAcknowledgment && (
                  <Box sx={{ mt: 3, mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Acknowledgment Required
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Your manager has marked this check-in as missed. Please acknowledge their comments below.
                    </Typography>
                  </Box>
                )}

                {canManagerConfirm && !canManagerRecordDecision && (
                  <Box sx={{ mt: 3, mb: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Notes (optional)"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add any notes about this check-in..."
                    />
                  </Box>
                )}

                {/* Audit Trail Section */}
                <Accordion sx={{ mt: 3 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1">Audit Trail</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {loadingAudit ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : auditEvents.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No audit events found.
                      </Typography>
                    ) : (
                      <Box>
                        {auditEvents.map((event) => (
                          <Box key={event.id} sx={{ mb: 2, pb: 2, borderBottom: '1px solid #eee' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="subtitle2">
                                {getEventTypeLabel(event.eventType)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(event.eventAt, { includeTime: true })}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              By: {event.actorName}
                            </Typography>
                            {event.details && (
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                {formatCheckInStatusInText(event.details)}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setDetailDialogOpen(false);
              setNotes('');
              setDecisionComments('');
              setDecisionType('Occurred');
              setActualOccurrenceDate(null);
            }}>Close</Button>
            {canManagerRecordDecision && (
              <Button
                variant="contained"
                color={decisionType === 'Missed' ? 'error' : 'primary'}
                onClick={handleMarkDecision}
                disabled={markingDecision || (decisionType === 'Occurred' ? !actualOccurrenceDate : !decisionComments.trim())}
                startIcon={<CheckCircleIcon />}
              >
                {decisionType === 'Occurred' ? 'Record occurred' : 'Record as missed'}
              </Button>
            )}
            {canConfirm && !canManagerRecordDecision && (
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={confirming}
                startIcon={<CheckCircleIcon />}
                sx={{ textTransform: 'none' }}
              >
                {isManager
                  ? 'Record Check-In Decision'
                  : needsAcknowledgment
                    ? 'Acknowledge Manager Comments'
                    : 'Acknowledge Check-In'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </AppLayout>
  );
}
